import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import {
  ActivityUnit,
  StationaryCombustionActivitiesList,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Unit from '@shared/components/content/unit';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { formatQuantity } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';

export function useStationaryCombustionTableColumns({
  filters,
}: {
  filters: FilterOptions<['timePeriods', 'sites', 'fuels']>;
}): ColumnDef<StationaryCombustionActivitiesList>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
      enableColumnFilter: true,
    },
    {
      id: 'period',
      accessorKey: 'period',
      enableHiding: true,
      meta: {
        label: 'Time period',
        variant: 'multiSelect',
        options: filters.timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },

      enableColumnFilter: true,
    },
    {
      id: 'site',
      accessorKey: 'site',
      enableColumnFilter: true,
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue }) => {
        const site = getValue<StationaryCombustionActivitiesList['site']>();
        return <TextCell text={site.name} />;
      },
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'fuel',
      accessorKey: 'fuel',
      header: ({ column }) => <HeaderCell column={column} name="Fuel" />,
      cell: ({ getValue }) => {
        const fuel = getValue<StationaryCombustionActivitiesList['fuel']>();
        return <TextCell text={fuel.name} />;
      },
      enableColumnFilter: true,
      meta: {
        label: 'Fuel',
        variant: 'multiSelect',
        options: filters.fuels,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },

    {
      accessorKey: 'emissionFactor',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission Factor"
          numberColumn
          unit={<Unit variant="kgCO2e/unit" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissionFactor = getValue<number>();
        return <NumberCell number={emissionFactor} />;
      },
    },
    {
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Quantity"
          numberColumn
          unit={<Unit variant="units" />}
        />
      ),
      cell: ({ getValue }) => {
        const quantity = getValue<number>();
        return <NumberCell number={quantity} formatter={formatQuantity} />;
      },
    },
    {
      accessorKey: 'unit',
      header: ({ column }) => <HeaderCell column={column} name="Unit" />,
      cell: ({ getValue }) => {
        const unit = getValue<ActivityUnit>();
        return <TextCell text={unit.symbol} />;
      },
      enableSorting: false,
    },
    {
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissions = getValue<number>();
        return <NumberCell number={emissions} />;
      },
    },
    {
      id: 'scope3Emissions',
      accessorKey: 'scope3Emissions',
      enableSorting: true,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Scope 3 Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
          badge={
            <SimpleTooltip
              trigger={<Icons.info size={16} />}
              content={
                <span>
                  Scope 3 emissions generated by your stationary combustion
                </span>
              }
            />
          }
        />
      ),
      meta: {
        sorting: { ascLabel: 'Lowest', descLabel: 'Highest' },
      },
      cell: ({ getValue }) => {
        const scope3Emissions = getValue<number>();
        return <NumberCell number={scope3Emissions} />;
      },
    },
  ];
}
